import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { AlignType } from 'common/types/styleTypes'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { useTypography } from 'src/editor/shared/hooks/use-typography'
import { Repeat04Icon } from 'src/editor/shared/icons/repeat-04-icon'
import {
  ButtonSizeEnum,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { BaseTextContentEntityInterface } from 'src/shared/types/base-entity-interface'
import { twMerge } from 'tailwind-merge'
import { FontSelectorControl } from '../../../controls/font-selector-control'
import { FontSizeControl } from '../../../controls/font-size-control'
import { LetterSpacingControl } from '../../../controls/letter-spacing-control'
import { LineHeightControl } from '../../../controls/line-height-control'
import { TextAlignControl } from '../../../controls/text-align-control'
import { useFontSelector } from '../../../hooks/use-font-selector'
import { ShowHideTypographyControlsType } from '../types'
import { calculateShowComponents } from '../utils'

export const EntityTypographyGroup = <
  FontSizeKey extends `${KeyPrefix}FontSize`,
  MobileFontSizeKey extends `mobile${Capitalize<KeyPrefix>}FontSize`,
  FontFamilyKey extends `${KeyPrefix}FontFamily`,
  MobileFontFamilyKey extends `mobile${Capitalize<KeyPrefix>}FontFamily`,
  FontLineHeight extends `${KeyPrefix}LineHeight`,
  MobileFontLineHeight extends `mobile${Capitalize<KeyPrefix>}LineHeight`,
  FontWeightKey extends `${KeyPrefix}FontWeight`,
  MobileFontWeightKey extends `mobile${Capitalize<KeyPrefix>}FontWeight`,
  FontStyleKey extends `${KeyPrefix}FontStyle`,
  MobileFontStyleKey extends `mobile${Capitalize<KeyPrefix>}FontStyle`,
  LetterSpacingKey extends `${KeyPrefix}LetterSpacing`,
  MobileLetterSpacingKey extends `mobile${Capitalize<KeyPrefix>}LetterSpacing`,
  TextAlignKey extends `${KeyPrefix}TextAlign`,
  MobileTextAlignKey extends `mobile${Capitalize<KeyPrefix>}TextAlign`,
  FontFileIdKey extends `${KeyPrefix}FontFileId`,
  MobileFontFileIdKey extends `mobile${Capitalize<KeyPrefix>}FontFileId`,
  Entity extends {
    [K in FontSizeKey]?: BaseTextContentEntityInterface['fontSize']
  } & {
    [K in MobileFontSizeKey]?: BaseTextContentEntityInterface['mobileFontSize']
  } & {
    [K in FontFamilyKey]?: BaseTextContentEntityInterface['fontFamily']
  } & {
    [K in MobileFontFamilyKey]?: BaseTextContentEntityInterface['mobileFontFamily']
  } & {
    [K in FontLineHeight]?: BaseTextContentEntityInterface['lineHeight']
  } & {
    [K in MobileFontLineHeight]?: BaseTextContentEntityInterface['mobileLineHeight']
  } & {
    [K in FontWeightKey]?: BaseTextContentEntityInterface['fontWeight']
  } & {
    [K in MobileFontWeightKey]?: BaseTextContentEntityInterface['mobileFontWeight']
  } & {
    [K in FontStyleKey]?: BaseTextContentEntityInterface['fontStyle']
  } & {
    [K in MobileFontStyleKey]?: BaseTextContentEntityInterface['mobileFontStyle']
  } & {
    [K in LetterSpacingKey]?: BaseTextContentEntityInterface['letterSpacing']
  } & {
    [K in MobileLetterSpacingKey]?: BaseTextContentEntityInterface['mobileLetterSpacing']
  } & {
    [K in TextAlignKey]?: BaseTextContentEntityInterface['textAlign']
  } & {
    [K in MobileTextAlignKey]?: BaseTextContentEntityInterface['mobileTextAlign']
  } & {
    [K in FontFileIdKey]?: BaseTextContentEntityInterface['fontFileId']
  } & {
    [K in MobileFontFileIdKey]?: BaseTextContentEntityInterface['mobileFontFileId']
  },
  KeyPrefix extends string = '',
>({
  entity,
  keyPrefix = '' as KeyPrefix,
  hideTitle,
  isFirst,
  showFontSelector: showFontSelectorFromProps,
  showFontSize: showFontSizeFromProps,
  showLineHeight: showLineHeightFromProps,
  showTextAlign: showTextAlignFromProps,
  showLetterSpacing: showLetterSpacingFromProps,
  hideFontSelector,
  hideFontSize,
  hideLineHeight,
  hideTextAlign,
  hideLetterSpacing,
}: {
  entity: Entity
  keyPrefix?: KeyPrefix
  hideTitle?: boolean
  isFirst?: boolean
} & ShowHideTypographyControlsType) => {
  const { isMobile } = useDeviceModeContext()

  const [
    showFontSelector,
    showFontSize,
    showLineHeight,
    showTextAlign,
    showLetterSpacing,
  ] = calculateShowComponents(
    [
      showFontSelectorFromProps,
      showFontSizeFromProps,
      showLineHeightFromProps,
      showTextAlignFromProps,
      showLetterSpacingFromProps,
    ],
    [
      hideFontSelector,
      hideFontSize,
      hideLineHeight,
      hideTextAlign,
      hideLetterSpacing,
    ],
  )

  const updateEntity = useUpdateEntity<Entity>()
  const updateProp = useUpdateProp<Entity>(entity)

  const getKeyAndValue = <
    BaseTextKey extends keyof BaseTextContentEntityInterface,
    MobileBaseEntityValue extends keyof BaseTextContentEntityInterface,
    Key extends keyof Entity,
    MobileKey extends keyof Entity,
    Entity extends Record<string, unknown> = Record<string, unknown>,
    KeyPrefix extends string = string,
  >(
    entity: Entity,
    keyPrefix: KeyPrefix,
    keyName: string,
  ) => {
    const key = `${keyPrefix}${
      keyPrefix ? keyName.charAt(0).toUpperCase() + keyName.slice(1) : keyName
    }` as Key
    const mobileKey =
      `mobile${keyPrefix.charAt(0).toUpperCase() + keyPrefix.slice(1)}${
        keyName.charAt(0).toUpperCase() + keyName.slice(1)
      }` as MobileKey
    return {
      key: key,
      mobileKey: mobileKey,
      value: entity[
        key as keyof Entity
      ] as unknown as BaseTextContentEntityInterface[BaseTextKey],
      mobileValue: entity[
        mobileKey as keyof Entity
      ] as unknown as BaseTextContentEntityInterface[MobileBaseEntityValue],
    } as const
  }

  const {
    key: fontSizeKey,
    mobileKey: mobileFontSizeKey,
    value: fontSize,
    mobileValue: mobileFontSize,
  } = getKeyAndValue<
    'fontSize',
    'mobileFontSize',
    FontSizeKey,
    MobileFontSizeKey
  >(entity, keyPrefix, 'fontSize')

  const {
    key: fontFamilyKey,
    value: fontFamily,
    mobileValue: mobileFontFamily,
  } = getKeyAndValue<
    'fontFamily',
    'mobileFontFamily',
    FontFamilyKey,
    MobileFontFamilyKey
  >(entity, keyPrefix, 'fontFamily')

  const {
    key: fontLineHeightKey,
    mobileKey: mobileLineHeightKey,
    value: lineHeight,
    mobileValue: mobileLineHeight,
  } = getKeyAndValue<
    'lineHeight',
    'mobileLineHeight',
    FontLineHeight,
    MobileFontLineHeight
  >(entity, keyPrefix, 'lineHeight')

  const {
    key: fontWeightKey,
    value: fontWeight,
    mobileValue: mobileFontWeight,
  } = getKeyAndValue<
    'fontWeight',
    'mobileFontWeight',
    FontWeightKey,
    MobileFontWeightKey
  >(entity, keyPrefix, 'fontWeight')

  const {
    key: fontStyleKey,
    value: fontStyle,
    mobileValue: mobileFontStyle,
  } = getKeyAndValue<
    'fontStyle',
    'mobileFontStyle',
    FontStyleKey,
    MobileFontStyleKey
  >(entity, keyPrefix, 'fontStyle')

  const {
    key: letterSpacingKey,
    mobileKey: mobileLetterSpacingKey,
    value: letterSpacing,
    mobileValue: mobileLetterSpacing,
  } = getKeyAndValue<
    'letterSpacing',
    'mobileLetterSpacing',
    LetterSpacingKey,
    MobileLetterSpacingKey
  >(entity, keyPrefix, 'letterSpacing')

  const {
    key: textAlignKey,
    mobileKey: mobileTextAlignKey,
    value: textAlign,
    mobileValue: mobileTextAlign,
  } = getKeyAndValue<
    'textAlign',
    'mobileTextAlign',
    TextAlignKey,
    MobileTextAlignKey
  >(entity, keyPrefix, 'textAlign')

  const { key: fontFileIdKey } = getKeyAndValue<
    'fontFileId',
    'mobileFontFileId',
    FontFileIdKey,
    MobileFontFileIdKey
  >(entity, keyPrefix, 'fontFileId')

  const showResetTypographyToGlobalButton =
    (isMobile ? mobileFontSize : fontSize) ||
    (isMobile ? mobileLineHeight : lineHeight) ||
    (isMobile ? mobileTextAlign : textAlign)

  const { inheritedFontTypeLabel, updateObjectFont: updateEntityFont } =
    useFontSelector({
      object: entity,
      fontFamilyKey,
      fontWeightKey,
      fontStyleKey,
      fontFileIdKey,
      updateObject: updateEntity,
    })

  const {
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedLetterSpacing,
    globalFontFamily,
  } = useTypography({
    fontSize,
    mobileFontSize,
    lineHeight,
    mobileLineHeight,
    textAlign,
    mobileTextAlign,
    letterSpacing,
    mobileLetterSpacing,
  })

  const onResetTypography = () => {
    if (isMobile) {
      updateEntity({
        ...entity,
        [mobileFontSizeKey]: undefined,
        [mobileLineHeightKey]: undefined,
        [mobileTextAlignKey]: undefined,
      })
    } else {
      updateEntity({
        ...entity,
        [fontSizeKey]: undefined,
        [fontLineHeightKey]: undefined,
        [textAlignKey]: undefined,
      })
    }
  }

  return (
    <>
      {!hideTitle && (
        <GroupTitle
          isFirst={isFirst}
          className="flex justify-between "
          afterChildren={
            showResetTypographyToGlobalButton && (
              <GhostIconButton
                className="!my-[-4px]"
                size={ButtonSizeEnum.Small}
                onClick={onResetTypography}
                icon={<Repeat04Icon />}
              />
            )
          }
        >
          typography
        </GroupTitle>
      )}
      <div
        className={twMerge(
          '[&>*]:!m-0 mt-4 grid grid-cols-2 gap-3',
          '[&>*:nth-child(1)]:col-span-2 [&>*:nth-child(2)]:col-span-2',
        )}
      >
        {showFontSelector && (
          <FontSelectorControl
            update={updateEntityFont}
            fontFamily={fontFamily}
            mobileFontFamily={mobileFontFamily}
            fontWeight={fontWeight}
            mobileFontWeight={mobileFontWeight}
            fontStyle={fontStyle}
            mobileFontStyle={mobileFontStyle}
            hideLabel
            globalFontFamily={globalFontFamily}
            inheritedFontTypeLabel={inheritedFontTypeLabel}
          />
        )}
        {showFontSize && (
          <FontSizeControl
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            update={updateProp(fontSizeKey) as (value: number) => void}
            mobileUpdate={
              updateProp(mobileFontSizeKey) as (value: number) => void
            }
            className="!col-span-1"
          />
        )}
        {showLineHeight && (
          <LineHeightControl
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            update={updateProp(fontLineHeightKey) as (value: number) => void}
            mobileUpdate={
              updateProp(mobileLineHeightKey) as (value: number) => void
            }
            className="!col-span-1"
          />
        )}
        {showTextAlign && (
          <TextAlignControl
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            update={updateProp(textAlignKey) as (value: AlignType) => void}
            mobileUpdate={
              updateProp(mobileTextAlignKey) as (value: AlignType) => void
            }
            className="!col-span-1"
          />
        )}
        {showLetterSpacing && (
          <LetterSpacingControl
            letterSpacing={calculatedLetterSpacing.desktop}
            update={updateProp(letterSpacingKey) as (value: number) => void}
            mobileUpdate={
              updateProp(mobileLetterSpacingKey) as (value: number) => void
            }
            wrapperClassName="!col-span-2"
          />
        )}
      </div>
    </>
  )
}
